import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { useLazyGetContractorQuery, useLazyGetContractorsQuery } from "store/api/contractorsApi"
import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { setContractor } from "store/tempDataSlise"
import { UITypes } from "types"

import { convertContractorsToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { EditContractorModal } from "modals/EditContractorModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import TableWrapper from "components/table/TableWrapper"

import { getContractorTableHeaderCells } from "./getContractorTableHeaderCells"
import Header from "./Header"
import { parseApiDataToContractor } from "./parseApiDataToContractor"

export const ContractorsV1 = () => {
  const dispatch = useAppDispatch()
  const statusOptions = useAppSelector(state => state.tempData.contractorAccountStatusOptions) || []

  const [showEditContractorModal, setShowEditContractorModal] = useState(false)
  const [triggerGetContractors, { isFetching, data }] = useLazyGetContractorsQuery()
  const [triggerGetContractor, { isFetching: isContractorFetching }] = useLazyGetContractorQuery()
  const [triggerGetTableColumns, { isFetching: isColumnsFetching, data: columnsData }] = useLazyGetTableColumnsQuery()

  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()

  const tableHeaderCells = useMemo((): UITypes.TableHeaderCell[] => {
    const headerCells = getContractorTableHeaderCells(statusOptions)

    return columnsData?.data?.config ? reorderTableHeaderCells(headerCells, columnsData?.data?.config) : headerCells
  }, [columnsData, statusOptions])

  const sortByTopType = useMemo(() => {
    if (Object.keys(urlQueryParams).length > 1) {
      return ""
    }

    if (urlQueryParams["top15"]) {
      return "Top 15"
    }

    if (urlQueryParams["bottom15"]) {
      return "Bottom 15"
    }

    return ""
  }, [urlQueryParams])

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await triggerGetContractors(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const onEditClick = async (id: number) => {
    try {
      const res = await triggerGetContractor(`${id}`)
      if (res.data?.data) {
        dispatch(setContractor(parseApiDataToContractor(res.data?.data)))
        setShowEditContractorModal(true)
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    triggerGetTableColumns({ list_name: "admin_contractors_list" })
  }, [])

  const getRequestParams = () => {
    if (urlQueryParams["top15"] || urlQueryParams["bottom15"]) {
      return {
        perPage: 15,
        page: 1,
        order_column: "installed",
        order_type: urlQueryParams["top15"] ? "DESC" : "ASC",
      }
    }

    return urlQueryParams
  }

  const handleClearAdditionalTags = () => {
    setUrlQueryParams({ top15: null, bottom15: null })
  }

  const getContractors = () => {
    if (Object.keys(urlQueryParams).length > 1 && (urlQueryParams["top15"] || urlQueryParams["bottom15"])) {
      handleClearAdditionalTags()
    } else {
      const params = getRequestParams()
      sendRequest(params)
    }
  }

  const handleContractorAddEditSuccess = () => {
    setShowEditContractorModal(false)
    getContractors()
  }

  useEffect(() => {
    getContractors()
  }, [urlQueryParams])

  return (
    <ContentWrapper title="Contractors">
      <Header
        tableHeaderCells={tableHeaderCells}
        meta={data?.meta}
        onAddContractorSuccess={handleContractorAddEditSuccess}
      />
      <TableWrapper
        tableName="contractors"
        columnsLoaded={!!columnsData}
        headerCells={tableHeaderCells}
        data={convertContractorsToTableRows(data?.data)}
        isLoading={isFetching || isContractorFetching || isColumnsFetching}
        onEditClick={onEditClick}
        skippedTags={sortByTopType ? ["orderColumn", "account_status_id"] : ["account_status_id"]}
        clearAdditionalTags={handleClearAdditionalTags}
        additionalTags={
          sortByTopType
            ? [
                {
                  label: "Contractors",
                  values: [sortByTopType],
                },
              ]
            : []
        }
      />
      {!sortByTopType && <Pagination meta={data?.meta} />}
      {showEditContractorModal && (
        <EditContractorModal
          onCancel={() => setShowEditContractorModal(false)}
          onSuccess={handleContractorAddEditSuccess}
        />
      )}
    </ContentWrapper>
  )
}
